/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * @copyright (C) 2023, zacostudio. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of zacostudio, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by zacostudio or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jhyoung75@gmail.com)
 *
 * @description
 *
 * @created at Sat Nov 18 2023
 **/

const styles: { [key: string]: string } = {
	block: `
		padding: 2px 5px 2px 5px;
		line-height: 1.3;
		text-align: center;
		font-family: "Operator Mono";
		font-size: 8pt;
		font-weight: bold !important;
		color: #fff;
	`,
	chunk: ``,
	start: `
		border-top-left-radius: 5px;
		border-top-right-radius: 0px;
		border-bottom-right-radius: 0px;
		border-bottom-left-radius: 5px;
	`,
	mid: ``,
	end: `
		border-top-left-radius: 0px;
		border-top-right-radius: 5px;
		border-bottom-right-radius: 5px;
		border-bottom-left-radius: 0px;
	`,
	chiptag: `
		font-weight: normal !important;
		background: #1a6daf;
	`,
	info: `
		background: #29b6f6;
		color: #000;
	`,
	debug: `
		background: #7e57c2;
	`,
	log: `
		background: #9ccc65;
		color: #000;
	`,
	warn: `
		background: #26a69a;
		color: #000;
	`,
	error: `
		background: #ff0000;
	`,
	group: `
		background: #61708a;
	`,
	file: `
		color: #9e9e9e;
		padding-left: 20px;
	`,
	reset: `
		color: currentColor;
		background-color: transparent;
	`
};

const consoleFn: { [key: string]: (...data: any[]) => void } = {};
const Logger = {
	install() {
		if (consoleFn["log"]) {
			return;
		}

		const useLogger = localStorage.getItem("useLogger") ? localStorage.getItem("useLogger") === "true" : true;
		if (useLogger) {
			console.debug(`Installing logger Lv.${Logger.useLogLevel}`);
			if (Logger.useLogLevel >= 4) {
				consoleFn["log"] = console.log;
				consoleFn["debug"] = console.debug;
				consoleFn["info"] = console.info;
				consoleFn["warn"] = console.warn;
				consoleFn["error"] = console.error;
				consoleFn["group"] = console.group;
				consoleFn["groupCollapsed"] = console.groupCollapsed;

				console.log = this.log;
				console.debug = this.debug;
				console.info = this.info;
				console.warn = this.warn;
				console.error = this.error;
				console.group = this.group;
				console.groupCollapsed = this.groupCollapsed;
			} else {
				console.log =
					console.debug =
					console.info =
					console.warn =
					console.error =
					console.group =
					console.groupCollapsed =
					console.table =
						// eslint-disable-next-line @typescript-eslint/no-unused-vars
						(...data: any[]) => {};
			}
		}
	},
	get tag() {
		try {
			return consoleFn["tag"] ? `${consoleFn["tag"]}:` : "";
		} catch {
			return "";
		}
	},
	chip(type: string, caller: string) {
		const header = [];
		header.push(`%c${type}%c${caller}%c`);
		const param = [];
		param.push(
			`${styles.block} ${styles.start} ${styles[type.toLowerCase()]}`,
			`${styles.block} ${styles.chiptag} ${styles.end}`,
			`${styles.reset}`
		);
		return { header, param };
	},
	caller(data: any) {
		try {
			const reg = /^#(.*?)#\s?/i;
			let result = data[0].match(reg)?.at(1)?.trim() || "--";
			if (result !== "--") {
				data[0] = data[0].replace(reg, "").trim();
				if (data[0] === "") {
					data.shift();
				}
			} else {
				const newReg = /^\[(.*?)\]\s?/i;
				result = data[0].match(newReg)?.at(1)?.trim() || "UNITY";
				if (result !== "UNITY") {
					result = "Unity:" + result;
					data[0] = data[0].replace(newReg, "").trim();
					if (data[0] === "") {
						data.shift();
					}
				}
			}
			return result;
		} catch {
			return "--";
		}
	},
	get useLogLevel() {
		const value = localStorage.getItem("useLogLevel");
		if (value === null) {
			return 5;
		}
		return Number(value);
	},
	log(...data: any[]) {
		if (Logger.useLogLevel >= 5) {
			const { header, param } = Logger.chip("LOG", Logger.caller(data));
			// if (data.filter((item) => typeof item === "string" && item.startsWith("[UnityCache]")).length >= 0) {
			// 	return;
			// }
			consoleFn["log"](header.join(" "), ...param, ...data);
		}
	},
	debug(...data: any[]) {
		if (Logger.useLogLevel >= 1) {
			const { header, param } = Logger.chip("DEBUG", Logger.caller(data));
			consoleFn["debug"](header.join(" "), ...param, ...data);
		}
	},
	info(...data: any[]) {
		if (Logger.useLogLevel >= 2) {
			const { header, param } = Logger.chip("INFO", Logger.caller(data));
			consoleFn["info"](header.join(" "), ...param, ...data);
		}
	},
	warn(...data: any[]) {
		if (Logger.useLogLevel >= 1) {
			const { header, param } = Logger.chip("WARN", Logger.caller(data));
			consoleFn["warn"](header.join(" "), ...param, ...data);
		}
	},
	error(...data: any[]) {
		if (Logger.useLogLevel >= 1) {
			const { header, param } = Logger.chip("ERROR", Logger.caller(data));
			consoleFn["error"](header.join(" "), ...param, ...data);
		}
	},
	group(...data: any[]) {
		if (Logger.useLogLevel >= 5) {
			const { header, param } = Logger.chip("GROUP", Logger.caller(data));
			consoleFn["group"](header.join(" "), ...param, ...data);
		}
	},
	groupCollapsed(...data: any[]) {
		if (Logger.useLogLevel >= 5) {
			const { header, param } = Logger.chip("GROUP", Logger.caller(data));
			consoleFn["groupCollapsed"](header.join(" "), ...param, ...data);
		}
	}
};
export default Logger;
